/**
 * Production-safe logging utility for the Behtari platform
 * Phase 1: Console.log cleanup and standardization
 */

type LogLevel = 'debug' | 'info' | 'warn' | 'error';

interface LogContext {
  component?: string;
  userId?: string;
  action?: string;
  metadata?: Record<string, any>;
  [key: string]: any; // Allow arbitrary properties
}

class BehtariLogger {
  private isDevelopment = import.meta.env.DEV;
  private isProduction = import.meta.env.PROD;

  private formatMessage(level: LogLevel, message: string, context?: LogContext): string {
    const timestamp = new Date().toISOString();
    const contextStr = context ? ` [${context.component || 'App'}]` : '';
    return `[${timestamp}]${contextStr} ${level.toUpperCase()}: ${message}`;
  }

  debug(message: string, context?: LogContext, data?: any) {
    if (this.isDevelopment) {

    }
  }

  info(message: string, context?: LogContext, data?: any) {
    if (this.isDevelopment) {

    } else if (this.isProduction) {
      // Send to analytics service in production
      this.sendToAnalytics('info', message, context, data);
    }
  }

  warn(message: string, context?: LogContext, data?: any) {
    if (this.isDevelopment) {
      console.warn(this.formatMessage('warn', message, context), data || '');
    } else if (this.isProduction) {
      this.sendToAnalytics('warn', message, context, data);
    }
  }

  error(message: string, context?: LogContext, error?: Error | any) {
    if (this.isDevelopment) {
      console.error(this.formatMessage('error', message, context), error || '');
    } else if (this.isProduction) {
      this.sendToAnalytics('error', message, context, error);
    }
  }

  // User action tracking (always enabled)
  userAction(action: string, context?: LogContext, data?: any) {
    const message = `User action: ${action}`;
    
    if (this.isDevelopment) {

    }
    
    // Always send user actions to analytics
    this.sendToAnalytics('info', message, context, data);
  }

  // Performance tracking
  performance(operation: string, duration: number, context?: LogContext) {
    const message = `Performance: ${operation} took ${duration.toFixed(2)}ms`;
    
    if (duration > 100) { // Only log slow operations
      if (this.isDevelopment) {
        console.warn(this.formatMessage('warn', message, context));
      } else if (this.isProduction) {
        this.sendToAnalytics('warn', message, context, { duration });
      }
    }
  }

  private sendToAnalytics(level: LogLevel, message: string, context?: LogContext, data?: any) {
    // Production analytics with fallback handling
    try {
      if (typeof window !== 'undefined' && (window as any).gtag) {
        (window as any).gtag('event', 'app_log', {
          level,
          message,
          component: context?.component,
          user_id: context?.userId
        });
      }
      
      // Also store in local analytics buffer for later sync
      this.storeLocalAnalytics(level, message, context, data);
    } catch (error) {
      // Silently handle analytics failures
      console.warn('Analytics logging failed:', error);
    }
  }

  private storeLocalAnalytics(level: LogLevel, message: string, context?: LogContext, data?: any) {
    try {
      const analyticsData = {
        level,
        message,
        context,
        data,
        timestamp: Date.now()
      };
      
      const existing = localStorage.getItem('behtari_analytics') || '[]';
      const logs = JSON.parse(existing);
      logs.push(analyticsData);
      
      // Keep only last 100 entries
      if (logs.length > 100) {
        logs.splice(0, logs.length - 100);
      }
      
      localStorage.setItem('behtari_analytics', JSON.stringify(logs));
    } catch (error) {
      // Handle storage failures silently
    }
  }
}

export const logger = new BehtariLogger();

// Convenience functions for common logging patterns
export const logUserAction = (action: string, component: string, data?: any) => {
  logger.userAction(action, { component }, data);
};

export const logError = (error: Error, component: string, context?: string) => {
  logger.error(`Error in ${context || 'operation'}`, { component }, error);
};

export const logPerformance = (operation: string, startTime: number, component: string) => {
  const duration = performance.now() - startTime;
  logger.performance(operation, duration, { component });
};

// Development-only logging functions (will be stripped in production)
export const devLog = (message: string, data?: any) => {
  if (import.meta.env.DEV) {

  }
};

export const devWarn = (message: string, data?: any) => {
  if (import.meta.env.DEV) {
    console.warn(`[DEV] ${message}`, data || '');
  }
};

export const devError = (message: string, data?: any) => {
  if (import.meta.env.DEV) {
    console.error(`[DEV] ${message}`, data || '');
  }
};